import {
  Activity,
  Bird,
  Cat,
  CircleDot,
  ClipboardList,
  Dog,
  MoreHorizontal,
  Pencil,
  Rabbit,
  Stethoscope,
  Tag,
  Trash2,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { PacienteResponse, PacienteResponseEspecie } from '@/api/generated/model';
import { BotaoFavorito } from '@/features/pacientes/components/BotaoFavorito';
import { VincularTagsDialogo } from '@/features/pacientes/components/VincularTagsDialogo';
import { CORES_SEXO, ESPECIES, formatarIdade, SEXOS } from '@/features/pacientes/lib/especies';
import { corDeTextoLegivel } from '@/shared/lib/cores';
import { rotaDaFichaClinica, rotaDoHistorico } from '@/shared/lib/rotas';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';

/**
 * Ícone por espécie. A V1 mapeava só felino, equino, bovino e "outro"
 * (`PacienteCard.tsx:12-24`) e caía em cachorro para o resto — inclusive para
 * ave e réptil, que ela nem tinha no cadastro.
 */
const ICONE: Partial<Record<PacienteResponseEspecie, typeof Dog>> = {
  FELINO: Cat,
  AVE: Bird,
  ROEDOR: Rabbit,
};

interface Props {
  paciente: PacienteResponse;
  podeEditar: boolean;
  podeExcluir: boolean;
  podeVerHistorico: boolean;
  /** Diagnósticos e peso. Chave PRÓPRIA — ver `PacientesPage`. */
  podeVerFicha: boolean;
  aoEditar: (paciente: PacienteResponse) => void;
  aoExcluir: (paciente: PacienteResponse) => void;
}

export function PacienteCard({
  paciente,
  podeEditar,
  podeExcluir,
  podeVerHistorico,
  podeVerFicha,
  aoEditar,
  aoExcluir,
}: Props) {
  const cores = CORES_SEXO[paciente.sexo];
  const Icone = ICONE[paciente.especie] ?? Dog;
  const [tagsAberto, setTagsAberto] = useState(false);
  // `podeEditar` também controla o menu de tags: vincular é uma edição de
  // cadastro do paciente (permissão `pacientes.editar` no backend).
  const temMenu = podeEditar || podeExcluir || podeVerHistorico || podeVerFicha;

  return (
    <div className={`bg-surface-raised rounded-lg p-5 border-2 transition-colors ${cores.borda}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          {paciente.fotoUrl ? (
            <img
              src={paciente.fotoUrl}
              alt={paciente.nome}
              className="w-10 h-10 rounded-lg object-cover border border-border shrink-0"
            />
          ) : (
            <span
              className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cores.fundo}`}
            >
              <Icone className={`w-5 h-5 ${cores.texto}`} />
            </span>
          )}

          <div className="min-w-0">
            {podeVerHistorico ? (
              <Link
                to={rotaDoHistorico(paciente.id)}
                className="font-semibold text-foreground text-sm hover:text-brand transition-colors block truncate"
              >
                {paciente.nome}
              </Link>
            ) : (
              <h3 className="font-semibold text-foreground text-sm truncate">{paciente.nome}</h3>
            )}
            <p className="text-xs text-muted-foreground">{ESPECIES[paciente.especie]}</p>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <BotaoFavorito paciente={paciente} className="h-8 w-8" />

          <Badge variant="outline" className={`text-xs font-semibold ${cores.texto}`}>
            {SEXOS[paciente.sexo]}
          </Badge>

          {temMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={`Ações de ${paciente.nome}`}>
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {podeVerHistorico && (
                  <DropdownMenuItem asChild>
                    <Link to={rotaDoHistorico(paciente.id)}>
                      <Activity className="w-4 h-4" />
                      Ver histórico
                    </Link>
                  </DropdownMenuItem>
                )}

                {podeVerFicha && (
                  <DropdownMenuItem asChild>
                    <Link to={rotaDaFichaClinica(paciente.id)}>
                      <Stethoscope className="w-4 h-4" />
                      Ficha clínica
                    </Link>
                  </DropdownMenuItem>
                )}

                {podeEditar && (
                  <DropdownMenuItem onClick={() => aoEditar(paciente)}>
                    <Pencil className="w-4 h-4" />
                    Editar
                  </DropdownMenuItem>
                )}

                {podeEditar && (
                  <DropdownMenuItem onClick={() => setTagsAberto(true)}>
                    <Tag className="w-4 h-4" />
                    Vincular a tags
                  </DropdownMenuItem>
                )}

                {podeExcluir && (
                  <DropdownMenuItem
                    onClick={() => aoExcluir(paciente)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                    Excluir
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <ul className="space-y-1.5 mb-4">
        {[
          paciente.raca,
          formatarIdade(paciente.idadeMeses) === '—' ? null : formatarIdade(paciente.idadeMeses),
          paciente.pesoKg === null ? null : `${paciente.pesoKg} kg`,
          paciente.castrado ? 'Castrado' : null,
        ]
          .filter((linha): linha is string => Boolean(linha))
          .map((linha) => (
            <li key={linha} className="flex items-center gap-2 text-xs text-muted-foreground">
              <CircleDot className="w-3 h-3 shrink-0" />
              <span className="truncate">{linha}</span>
            </li>
          ))}
      </ul>

      {(paciente.grupos.length > 0 || paciente.tags.length > 0) && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {/* `asChild` precisa de um ref de verdade pra ancorar a posição — `Badge`
              não usa `React.forwardRef` (só função simples), então o span aqui é o
              alvo real do trigger, não decoração. Mesmo problema já evitado em
              `ComparacaoDeGrupos.tsx`, que usa `<button>` puro em vez de um
              componente custom como filho direto do `TooltipTrigger`. */}
          {paciente.grupos.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Badge variant="outline" className="text-xs gap-1 cursor-default">
                    <Users className="w-3 h-3" />
                    Grupo{paciente.grupos.length > 1 ? ` · ${paciente.grupos.length}` : ''}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex max-w-56 flex-wrap gap-1">
                  {paciente.grupos.map((grupo) => (
                    <span
                      key={grupo.id}
                      className="inline-block truncate rounded px-2 py-0.5 text-xs font-medium max-w-full"
                      style={{ backgroundColor: grupo.cor, color: corDeTextoLegivel(grupo.cor) }}
                    >
                      {grupo.nome}
                    </span>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Separado do "Grupo" de propósito: são conceitos diferentes (tag de
              cadastro × coorte de análise) — ver o comentário em `TagDePaciente`
              no schema do backend. */}
          {paciente.tags.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Badge variant="outline" className="text-xs gap-1 cursor-default">
                    <Tag className="w-3 h-3" />
                    Tags{paciente.tags.length > 1 ? ` · ${paciente.tags.length}` : ''}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex max-w-56 flex-wrap gap-1">
                  {paciente.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-block truncate rounded px-2 py-0.5 text-xs font-medium max-w-full"
                      style={{ backgroundColor: tag.cor, color: corDeTextoLegivel(tag.cor) }}
                    >
                      {tag.nome}
                    </span>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}

      <div className="pt-3 border-t border-border">
        <p className="text-xs font-semibold uppercase tracking-wide mb-0.5 text-muted-foreground">
          Responsável
        </p>
        <p className="text-sm font-medium text-foreground truncate">{paciente.tutorNome ?? '—'}</p>
        {/* A V1 não tinha estes números, mas a TABELA tem. Trocar de modo não
            pode perder informação, então eles ficam — discretos, fora do lugar
            que os dois botões ocupam. */}
        <p className="text-xs text-muted-foreground mt-1">
          {paciente.totalExames} exames · {paciente.totalProntuarios} prontuários
        </p>
      </div>

      {/* Os dois botões do rodapé são a assinatura do card da V1
          (`PacienteCard.tsx:134-154`): "Complementar" em outline à esquerda e,
          à direita, o botão cheio pintado com a cor do sexo.

          Como na V1, "Prontuário" abre o histórico JÁ na aba de prontuário —
          lá era `Historico?paciente=X&view=prontuario`, aqui é `?aba=`. */}
      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-border">
        {podeEditar ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => aoEditar(paciente)}
            className="gap-1 h-8 text-xs"
          >
            <Pencil className="w-3.5 h-3.5" />
            Complementar dados
          </Button>
        ) : (
          <span />
        )}

        {podeVerHistorico && (
          <Button asChild size="sm" className={`w-full gap-1 h-8 text-xs ${cores.botao}`}>
            <Link to={rotaDoHistorico(paciente.id, 'prontuario')}>
              <ClipboardList className="w-3.5 h-3.5" />
              Prontuário
            </Link>
          </Button>
        )}
      </div>

      {podeEditar && (
        <VincularTagsDialogo
          aberto={tagsAberto}
          aoFechar={() => setTagsAberto(false)}
          paciente={paciente}
        />
      )}
    </div>
  );
}
